import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';
import { WasteEntry } from '../queries/waste.queries';

export interface RecordWasteInput {
  ingredientId: string;
  quantity: number;
  category: 'expired' | 'damaged' | 'spillage' | 'quality_issue' | 'over_production' | 'other';
  reason: string;
  photoUrl?: string;
}

export interface ApproveWasteInput {
  approved: boolean;
  notes?: string;
}

// Record new waste entry
export function useRecordWaste(branchId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: RecordWasteInput) => {
      const response = await apiClient.post<APIResponse<{ entry: WasteEntry }>>(
        '/waste',
        { ...input, branchId }
      );
      return response.data.data.entry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waste'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

// Approve or reject waste entry (supervisor only)
export function useApproveWaste(entryId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ApproveWasteInput) => {
      const response = await apiClient.patch<APIResponse<{ entry: WasteEntry }>>(
        `/waste/${entryId}/approve`,
        input
      );
      return response.data.data.entry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waste'] });
    },
  });
}

// Delete waste entry (before approval)
export function useDeleteWaste(entryId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete<APIResponse<{ success: boolean }>>(
        `/waste/${entryId}`
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waste'] });
    },
  });
}
